import { toast } from 'sonner';
import { checkJWTNotExpired, parseJWTFromCookie } from './jwtAuthTools';
import { omit } from '.';

const userLevelCtx = 'global';

const fetchWithDefaults = (
  url: string,
  method: 'POST' | 'GET' | 'PATCH' | 'PUT' | 'DELETE' = 'GET',
  body?: string,
  contentType?: 'text/plain' | 'application/json',
) => {
  const expired = !checkJWTNotExpired();

  if (expired) {
    toast.error('JWT expired. Please login again.');
    return new Promise(resolve => {
      resolve(null);
    });
  }

  const headers = {
    'Content-Type': contentType ?? 'application/json',
    neurojwt: parseJWTFromCookie() || '',
  };

  return fetch(url, { method, headers, body })
    .then(res => {
      if (res.ok)
        return res.json().catch(() => ({})); // Catch in case there is no response body
      else throw new Error(`Failed ${method} on ${url}. Status: ${res.status}.`);
    })
    .catch((err: Error) => {
      console.log(err);
      toast.error(err.message);
      return null;
    });
};

/**
 * Organization api fetchers
 */

// Get org list
export const getOrgList = async (): Promise<Array<IOrg> | null> => {
  const fetch = fetchWithDefaults('/api/admin/global/organizations').then(res => {
    return res;
  });
  return fetch;
};

// Get org data for single org
export const getOrgData = async (orgId: string): Promise<TVaultOrganization | null> => {
  const fetch = fetchWithDefaults(
    userLevelCtx === 'global' ? `/api/admin/global/organizations/${orgId}` : '/api/admin/local/organization',
  ).then(res => {
    return res;
  });
  return fetch;
};

// Post organization data for an organization
export const postOrganization = async (id: string, data: Omit<TVaultOrganization, 'orgId'>): Promise<boolean> => {
  const orgId = id;

  const body = JSON.stringify(data);
  const fetch = fetchWithDefaults(`/api/admin/global/organizations/${orgId}`, 'POST', body).then(res => {
    return !!res;
  });
  return fetch;
};

// Patch organization data
export const patchOrganization = async (id: string, data: Partial<TVaultOrganization>): Promise<boolean> => {
  const orgId = id;

  const body = JSON.stringify(data);
  const fetch = fetchWithDefaults(`/api/admin/global/organizations/${orgId}`, 'PATCH', body).then(res => {
    return !!res;
  });
  return fetch;
};

/**
 * Orgsettings
 */

// Get org settings for an organization
export const getOrgSettings = async (
  id: string,
): Promise<{ org: string; application: string; settings: IOrgSettings }[] | null> => {
  const orgId = id;

  const fetch = fetchWithDefaults(`/api/admin/global/settings/org/${orgId}`).then(res => {
    return res;
  });
  return fetch;
};

// Post org settings for an organization
export const postOrgSettings = async (
  id: string,
  application: string,
  data: IOrgSettings | string,
): Promise<boolean> => {
  const orgId = id;

  const body = typeof data === 'string' ? data : JSON.stringify(data);
  const fetch = fetchWithDefaults(`/api/admin/global/settings/org/${orgId}/${application}`, 'POST', body).then(res => {
    return !!res;
  });
  return fetch;
};

/**
 * User api fetchers
 */

export const getUserData = async (orgId?: string | null): Promise<Array<TVaultUser> | null> => {
  const fetch = fetchWithDefaults(
    userLevelCtx === 'global' ? `/api/admin/global/users/${orgId}` : '/api/admin/local/users',
  ).then(res => {
    return res;
  });
  return fetch;
};

export const postUserData = async (data: TVaultUser, orgId?: string | null): Promise<boolean | null> => {
  const formattedData = { ...data, lang: data.uILanguage };

  const fetch = fetchWithDefaults(
    userLevelCtx === 'global' ? `/api/admin/global/users/${orgId}` : `/api/admin/local/users`,
    'POST',
    JSON.stringify(formattedData),
  ).then(res => {
    return !!res;
  });
  return fetch;
};

export const patchUserData = async (
  userId: string,
  data: TVaultUser,
  orgId?: string | null,
): Promise<boolean | null> => {
  // Patch handler does not allow userId in the body
  const omittedData = omit(['userId', 'organization', 'accessGroups'], data);

  const fetch = fetchWithDefaults(
    userLevelCtx === 'global' ? `/api/admin/global/users/${orgId}/${userId}` : `/api/admin/local/users/${userId}`,
    'PATCH',
    JSON.stringify(omittedData),
  ).then(res => {
    return !!res;
  });
  return fetch;
};

export const deleteUserData = async (userId: string, orgId?: string | null): Promise<boolean | null> => {
  const fetch = fetchWithDefaults(
    userLevelCtx === 'global' ? `/api/admin/global/users/${orgId}/${userId}` : `/api/admin/local/users/${userId}`,
    'DELETE',
  ).then(res => {
    return !!res;
  });
  return fetch;
};

/**
 * Alt id:s
 */

export const getAltIDs = async (userId: string, orgId?: string | null): Promise<Array<IAltID> | null> => {
  const fetch = fetchWithDefaults(
    userLevelCtx === 'global' ? `/api/admin/global/ids/${orgId}/${userId}` : `/api/admin/local/ids/${userId}`,
  ).then(res => {
    return res;
  });
  return fetch;
};

export const postAltID = async (userId: string, data: IAltID, orgId?: string | null): Promise<IAltID | null> => {
  const fetch = fetchWithDefaults(
    userLevelCtx === 'global' ? `/api/admin/global/ids/${orgId}/${userId}` : `/api/admin/global/ids/${userId}`,
    'POST',
    JSON.stringify(data),
  ).then(res => {
    return res;
  });
  return fetch;
};

export const patchAltID = async (
  userId: string,
  altIDId: string,
  data: IAltID,
  orgId?: string | null,
): Promise<boolean | null> => {
  const fetch = fetchWithDefaults(
    userLevelCtx === 'global'
      ? `/api/admin/global/ids/${orgId}/${userId}/${altIDId}`
      : `/api/admin/local/ids/${userId}/${altIDId}`,
    'PATCH',
    JSON.stringify(data),
  ).then(res => {
    return !!res;
  });
  return fetch;
};

export const deleteAltID = async (altIDID: string, userId: string, orgId: string): Promise<boolean | null> => {
  const fetch = fetchWithDefaults(
    userLevelCtx === 'global'
      ? `/api/admin/global/ids/${orgId}/${userId}/${altIDID}`
      : `/api/admin/local/ids/${altIDID}`,
    'DELETE',
  ).then(res => {
    return !!res;
  });
  return fetch;
};

/**
 * Access groups
 */

export const getAccessGroups = async (orgId?: string | null): Promise<Array<TVaultAccessGroup> | null> => {
  const fetch = fetchWithDefaults(
    userLevelCtx === 'global' ? `/api/admin/global/accessgroups/${orgId}` : '/api/admin/local/accessgroups',
  ).then(res => {
    return res;
  });
  return fetch;
};

export const postAccessGroup = async (data: TVaultAccessGroup, orgId?: string | null): Promise<boolean | null> => {
  const fetch = fetchWithDefaults(
    userLevelCtx === 'global' ? `/api/admin/global/accessgroups/${orgId}` : `/api/admin/global/accessgroups/`,
    'POST',
    JSON.stringify(data),
  ).then(res => {
    return !!res;
  });
  return fetch;
};

export const patchAccessGroup = async (
  groupId: string,
  data: TVaultAccessGroup,
  orgId?: string | null,
): Promise<boolean | null> => {
  const fetch = fetchWithDefaults(
    userLevelCtx === 'global'
      ? `/api/admin/global/accessgroups/${orgId}/${groupId}`
      : `/api/admin/local/accessgroups/${groupId}`,
    'PATCH',
    JSON.stringify(data),
  ).then(res => {
    return !!res;
  });
  return fetch;
};

export const deleteAccessGroup = async (groupId: string, orgId?: string | null): Promise<boolean | null> => {
  const fetch = fetchWithDefaults(
    userLevelCtx === 'global'
      ? `/api/admin/global/accessgroups/${orgId}/${groupId}`
      : `/api/admin/local/accessgroups/${groupId}`,
    'DELETE',
  ).then(res => {
    return !!res;
  });
  return fetch;
};

/**
 * Global settings
 */

export const getGlobalSettings = async (): Promise<Array<{
  application: string;
  settings: Record<string, any>;
}> | null> => {
  const fetch = fetchWithDefaults(`/api/admin/global/settings/app`).then(res => {
    return res;
  });
  return fetch;
};

export const putGlobalSettings = async (app: string, fieldName: string, data: any): Promise<boolean | null> => {
  const fetch = fetchWithDefaults(
    `/api/admin/global/settings/app/${app}/${fieldName}`,
    'PUT',
    JSON.stringify(data),
  ).then(res => {
    return !!res;
  });
  return fetch;
};

export const deleteGlobalSettings = async (app: string, fieldName: string): Promise<boolean | null> => {
  const fetch = fetchWithDefaults(`/api/admin/global/settings/app/${app}/${fieldName}`, 'DELETE').then(res => {
    return !!res;
  });
  return fetch;
};

// Access rules
export const getAccessRules = async (orgId?: string, userId?: string | null): Promise<Array<any> | null> => {
  const oid = orgId ? '/' + orgId : '';
  const uid = userId ? '/' + userId : '';
  const fetch = fetchWithDefaults(`/api/admin/global/accessrules${oid}${uid}`).then(res => {
    return res;
  });
  return fetch;
};

export const putAccessRules = async (
  app: string,
  data: string,
  orgId?: string,
  userId?: string,
): Promise<boolean | null> => {
  const oid = orgId ? '/' + orgId : '';
  const uid = userId ? '/' + userId : '';
  const fetch = fetchWithDefaults(`/api/admin/global/accessrules/${app}${oid}${uid}`, 'PUT', data, 'text/plain').then(
    res => {
      return !!res;
    },
  );
  return fetch;
};

export const logOut = async (): Promise<boolean> => {
  const fetch = fetchWithDefaults(`/auth/v0/logout`, 'GET').then(res => {
    return !!res;
  });
  return fetch;
};
